import React, { useCallback, useEffect, useMemo, useState } from "react";
import API_BASE from "./apiBase";
import { useMaskNames } from "./nameMask";

/**
 * VEHICLES - the fleet's vans, as recorded in the app.
 *
 * Two pieces. VehiclesCard is the small dashboard tile: one line per date
 * parameter (MOT, service, insurance, car tax) coloured by how close the
 * nearest renewal across the fleet is - green when everything is in date,
 * amber when something falls due within a month, red when something is due
 * within two weeks or is already overdue. Its border takes the worst colour.
 * Clicking it opens VehiclesView, the full-screen list: every vehicle, its
 * site, its owner when a driver has been assigned, and its parameters, each
 * row editable in place.
 *
 * Saving goes through the same server rules the app uses (PATCH
 * /api/headoffice/vehicles/:id delegates to the driver vehicle service), so
 * a registration typed here is normalised and checked for a clash at that
 * branch exactly as one typed on the handset.
 */
const token = () => localStorage.getItem("token") || localStorage.getItem("authToken") || "";
async function api(path, method = "GET", body) {
  const res = await fetch(`${API_BASE}/api/headoffice/vehicles${path}`, {
    method,
    headers: { Authorization: `Bearer ${token()}`, "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d?.error?.message || (typeof d?.error === "string" ? d.error : null) || `HTTP ${res.status}`);
  return d;
}

// The four dates, in the order the office reads them.
const DATE_FIELDS = [
  ["mot_due_at", "MOT"],
  ["service_due_at", "Service"],
  ["insurance_due_at", "Insurance"],
  ["tax_date", "Car tax"],
];
const DAY = 86400000;

/**
 * The rule, in one place: red at 14 days or overdue, amber at 30, green
 * beyond; a missing date is "unknown", which is amber - a van with no MOT
 * date on file is not a van you can call in date.
 */
function urgency(value) {
  if (!value) return "unknown";
  const days = Math.floor((new Date(value).getTime() - Date.now()) / DAY);
  if (days < 14) return "red";
  if (days <= 30) return "amber";
  return "green";
}
const RANK = { red: 3, amber: 2, unknown: 1, green: 0 };
const worst = (list) => list.reduce((w, u) => (RANK[u] > RANK[w] ? u : w), "green");
const COLOUR = {
  red: { border: "#ef4444", bg: "#fde2e1", text: "#b91c1c", word: "overdue or due within 2 weeks" },
  amber: { border: "#f59e0b", bg: "#fef3c7", text: "#b45309", word: "due within a month" },
  unknown: { border: "#f59e0b", bg: "#fef3c7", text: "#b45309", word: "date not recorded" },
  green: { border: "#22c55e", bg: "#dcfce7", text: "#059669", word: "all in date" },
};
const fmtDate = (v) => (v
  ? new Date(v).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
  : "—");
const toInputDate = (v) => (v ? String(v).slice(0, 10) : "");

/** Load every vehicle in scope, flattened, with its branch name attached. */
function useVehicles() {
  const [branches, setBranches] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    try { setBranches((await api("")).branches || []); setError(null); }
    catch (e) { setError(e.message); setBranches([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const vehicles = useMemo(
    () => (branches || []).flatMap((b) => b.vehicles.map((v) => ({ ...v, branch: b.branch }))),
    [branches],
  );
  return { branches, vehicles, error, reload: load };
}

// ---------------------------------------------------------------------------
// The dashboard tile
// ---------------------------------------------------------------------------
const T = {
  card: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6, boxSizing: "border-box",
          padding: 11, cursor: "pointer", minWidth: 0, overflowWrap: "anywhere" },
  title: { fontSize: 14, fontWeight: 600, marginBottom: 5, color: "#111827", lineHeight: 1.25 },
  blurb: { fontSize: 11, color: "#6b7280", marginBottom: 8, lineHeight: 1.4 },
  row: { display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8,
         fontSize: 12.5, color: "#374151", padding: "2px 6px", lineHeight: 1.3, borderRadius: 4,
         margin: "0 -6px" },
};

export default function VehiclesCard({ onOpen }) {
  const { branches, vehicles, error } = useVehicles();
  const active = vehicles.filter((v) => v.is_active);

  // Per parameter: the worst urgency across the active fleet, and how many
  // vehicles are at that level, so "Car tax  2 due" reads at a glance.
  const perField = DATE_FIELDS.map(([key, label]) => {
    const levels = active.map((v) => urgency(v[key]));
    const w = worst(levels);
    const n = levels.filter((u) => u === w).length;
    return { key, label, worst: w, count: n };
  });
  const overall = branches === null ? "unknown" : worst(perField.map((f) => f.worst));
  const border = branches === null ? "#f59e0b" : COLOUR[overall].border;

  return (
    <div style={{ ...T.card, border: `3px solid ${border}` }} onClick={() => onOpen && onOpen()}
         title="Every vehicle recorded from the app. Click to list them by site and edit their details.">
      <div style={T.title}>
        Vehicles
        <span style={{ fontWeight: 400, color: "#6b7280" }}> ({branches === null ? "…" : active.length})</span>
      </div>
      <div style={T.blurb}>
        Renewal dates across the fleet. Green in date, amber within a month, red within two weeks or overdue.
      </div>
      {perField.map((f) => {
        const c = COLOUR[branches === null ? "unknown" : f.worst];
        const loaded = branches !== null;
        return (
          <div key={f.key} style={{ ...T.row, background: loaded ? c.bg : undefined }}
               title={loaded ? `${f.label}: ${c.word}` : ""}>
            <span>{f.label}</span>
            <span style={{ color: loaded ? c.text : "#6b7280", fontWeight: 600, whiteSpace: "nowrap" }}>
              {!loaded ? "…"
                : active.length === 0 ? "—"
                : f.worst === "green" ? "In date"
                : f.worst === "unknown" ? `${f.count} not set`
                : `${f.count} ${f.worst === "red" ? "due / overdue" : "due"}`}
            </span>
          </div>
        );
      })}
      {error && <div style={{ color: "#b91c1c", fontSize: 12, padding: "4px 0" }}>{error}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The full-screen list
// ---------------------------------------------------------------------------
const V = {
  wrap: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6, padding: 16, marginTop: 12 },
  head: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10 },
  h: { fontSize: 16, fontWeight: 700, color: "#111827" },
  muted: { fontSize: 12, color: "#6b7280" },
  th: { textAlign: "left", fontSize: 11, fontWeight: 800, color: "#6b7280", padding: "6px 8px",
        borderBottom: "2px solid #e5e7eb", textTransform: "uppercase", letterSpacing: 0.3, whiteSpace: "nowrap" },
  td: { fontSize: 13, padding: "6px 8px", borderBottom: "1px solid #f1f5f9", verticalAlign: "top" },
  inp: { border: "1px solid #d1d5db", borderRadius: 4, padding: "4px 7px", fontSize: 13, width: "100%",
         boxSizing: "border-box" },
  btn: (c, solid) => ({ background: solid ? c : "#fff", color: solid ? "#fff" : c, border: `1px solid ${c}`,
    borderRadius: 5, padding: "5px 11px", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }),
  pill: (u) => ({ display: "inline-block", padding: "1px 7px", borderRadius: 10, fontSize: 11.5, fontWeight: 700,
    background: COLOUR[u].bg, color: COLOUR[u].text, whiteSpace: "nowrap" }),
  error: { color: "#b91c1c", fontSize: 12.5, marginTop: 6 },
};

function DateCell({ value }) {
  const u = urgency(value);
  if (!value) return <span style={V.pill("unknown")}>not set</span>;
  const days = Math.floor((new Date(value).getTime() - Date.now()) / DAY);
  const note = days < 0 ? `${-days}d overdue` : days <= 30 ? `${days}d left` : null;
  return (
    <span>
      <span style={{ fontWeight: u === "green" ? 500 : 800, color: COLOUR[u].text }}>{fmtDate(value)}</span>
      {note && <span style={{ ...V.pill(u), marginLeft: 6 }}>{note}</span>}
    </span>
  );
}

function VehicleRow({ v, masked, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({});

  const start = () => {
    setForm({
      registration_number: v.registration_number || "",
      description: v.description || "",
      mot_due_at: toInputDate(v.mot_due_at),
      service_due_at: toInputDate(v.service_due_at),
      insurance_due_at: toInputDate(v.insurance_due_at),
      tax_date: toInputDate(v.tax_date),
      is_active: v.is_active,
    });
    setErr(null); setEditing(true);
  };
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      // Only what changed goes over: the server treats an absent field as
      // "leave alone" and an explicit null as "clear the date".
      const patch = {};
      for (const k of ["registration_number", "description"]) {
        if ((form[k] || "") !== (v[k] || "")) patch[k] = form[k];
      }
      for (const [k] of DATE_FIELDS) {
        if (form[k] !== toInputDate(v[k])) patch[k] = form[k] ? form[k] : null;
      }
      if (form.is_active !== v.is_active) patch.is_active = form.is_active;
      if (!Object.keys(patch).length) { setEditing(false); setBusy(false); return; }
      await api(`/${v.id}`, "PATCH", patch);
      setEditing(false);
      await onSaved();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const owner = v.driver_name ? (masked ? "Driver" : v.driver_name) : null;

  if (!editing) {
    return (
      <tr style={{ opacity: v.is_active ? 1 : 0.55 }}>
        <td style={{ ...V.td, fontWeight: 800, whiteSpace: "nowrap" }}>{v.registration_number}</td>
        <td style={V.td}>{masked ? `Branch ${v.account_id}` : v.branch}</td>
        <td style={V.td}>{owner || <span style={{ color: "#9ca3af" }}>No driver assigned</span>}</td>
        <td style={V.td}>{v.description || <span style={{ color: "#9ca3af" }}>—</span>}</td>
        {DATE_FIELDS.map(([k]) => <td key={k} style={{ ...V.td, whiteSpace: "nowrap" }}><DateCell value={v[k]} /></td>)}
        <td style={V.td}>{v.is_active ? "Active" : "Retired"}</td>
        <td style={{ ...V.td, textAlign: "right" }}>
          <button style={V.btn("#0f766e")} onClick={start}>Edit</button>
        </td>
      </tr>
    );
  }
  return (
    <tr style={{ background: "#f0fdfa" }}>
      <td style={V.td}><input style={{ ...V.inp, width: 110, textTransform: "uppercase" }} value={form.registration_number} onChange={set("registration_number")} /></td>
      <td style={V.td}>{masked ? `Branch ${v.account_id}` : v.branch}</td>
      <td style={V.td}>{owner || <span style={{ color: "#9ca3af" }}>No driver assigned</span>}</td>
      <td style={V.td}><input style={V.inp} value={form.description} onChange={set("description")} placeholder="e.g. White Transit, long wheelbase" /></td>
      {DATE_FIELDS.map(([k]) => (
        <td key={k} style={V.td}><input type="date" style={{ ...V.inp, width: 140 }} value={form[k]} onChange={set(k)} /></td>
      ))}
      <td style={V.td}>
        <select style={V.inp} value={form.is_active ? "1" : "0"} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.value === "1" }))}>
          <option value="1">Active</option>
          <option value="0">Retired</option>
        </select>
      </td>
      <td style={{ ...V.td, textAlign: "right", whiteSpace: "nowrap" }}>
        <button style={V.btn("#6b7280")} disabled={busy} onClick={() => setEditing(false)}>Cancel</button>{" "}
        <button style={V.btn("#0f766e", true)} disabled={busy} onClick={save}>{busy ? "Saving…" : "Save"}</button>
        {err && <div style={V.error}>{err}</div>}
      </td>
    </tr>
  );
}

export function VehiclesView({ onClose }) {
  const masked = useMaskNames();
  const { branches, vehicles, error, reload } = useVehicles();
  const [showRetired, setShowRetired] = useState(false);
  const [query, setQuery] = useState("");

  const shown = vehicles
    .filter((v) => showRetired || v.is_active)
    .filter((v) => {
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return [v.registration_number, v.description, v.branch, v.driver_name].some((x) => (x || "").toLowerCase().includes(q));
    })
    // Worst first: the van that needs attention is at the top.
    .sort((a, b) => RANK[worst(DATE_FIELDS.map(([k]) => urgency(b[k])))] - RANK[worst(DATE_FIELDS.map(([k]) => urgency(a[k])))]
      || a.branch.localeCompare(b.branch) || a.registration_number.localeCompare(b.registration_number));

  return (
    <div style={V.wrap}>
      <div style={V.head}>
        <div>
          <div style={V.h}>Vehicles</div>
          <div style={V.muted}>
            Every vehicle recorded from the app, worst renewal first. Owner is the driver it is assigned to.
            Edit any row in place; dates go red within two weeks or overdue, amber within a month.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input style={{ ...V.inp, width: 220 }} placeholder="Search reg, branch, driver…" value={query} onChange={(e) => setQuery(e.target.value)} />
          <label style={{ ...V.muted, whiteSpace: "nowrap", cursor: "pointer" }}>
            <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} /> show retired
          </label>
          <button style={V.btn("#6b7280")} onClick={reload}>Refresh</button>
          <button style={V.btn("#0f766e", true)} onClick={onClose}>Close</button>
        </div>
      </div>
      {error && <div style={V.error}>{error}</div>}
      {branches === null && <div style={V.muted}>Loading…</div>}
      {branches !== null && shown.length === 0 && !error && (
        <div style={V.muted}>
          {vehicles.length === 0
            ? "No vehicles recorded yet. A driver adds one from the app (Settings, Vehicle)."
            : "Nothing matches."}
        </div>
      )}
      {shown.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={V.th}>Reg</th><th style={V.th}>Site</th><th style={V.th}>Owner</th><th style={V.th}>Description</th>
              {DATE_FIELDS.map(([k, l]) => <th key={k} style={V.th}>{l}</th>)}
              <th style={V.th}>Status</th><th style={V.th}></th>
            </tr>
          </thead>
          <tbody>
            {shown.map((v) => <VehicleRow key={v.id} v={v} masked={masked} onSaved={reload} />)}
          </tbody>
        </table>
      )}
    </div>
  );
}
